import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { ownProductsAPI, offersAPI, peekCache } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { availabilityBadge, ORDINARY_AVAILABILITY } from '../styles/layout';
import { useChartColors } from '../contexts/ThemeContext';
import {
    PageHeader, SearchField, Pager, Alert, Tile, Modal, FileButton, Icon, SourceDot,
    sourceColor, saveBlob, fmtNum, fmtMoney, fmtPct, fmtDay, timeAgo, syncServerClock, seasonLabel,
} from '../ui';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './OwnProductsPage.css';

const PAGE_SIZE = 50;

// Відправляємо лише потрібні колонки: у файлі їх 20 і 46 тис. рядків,
// цілком він у тіло запиту не поміщається (ліміт nginx — 50 МБ).
// «Цена выхода» потрібна серверу, щоб знайти постачальника, від якого
// рахується ціна продажу; без неї закупівля бралась би в найдешевшого.
const IMPORT_COLUMNS = ['Id товара', 'Название товара', 'Цена продажи',
                        'Цена входа сконвертированная', 'Цена входа', 'Цена выхода',
                        'Кол-во', 'Поставщик'];

const LIST_TITLES = {
    '': 'Усі товари',
    matched: 'Зіставлені з ринком',
    unmatched: 'Без цін конкурентів',
    cheaper: 'Дешевші за ринок',
    even: 'В ринку',
    dearer: 'Дорожчі за ринок',
};

const POSITION_CHIP = { cheaper: 'good', dearer: 'bad', even: '' };

// Позиція нашої ціни відносно найдешевшої свіжої ціни конкурента.
const PositionChip = ({ product }) => {
    if (product.market_pos == null) {
        return <span className="muted" style={{ fontSize: 12.5 }}>{product.market_min == null ? 'немає даних' : '—'}</span>;
    }
    const kind = POSITION_CHIP[product.market_pos];
    return (
        <span className={`ui-chip${kind ? ` ui-chip--${kind}` : ''}`}
              title={`Найдешевший конкурент: ${fmtMoney(product.market_min)}`}>
            {product.market_pos === 'even' ? '≈ ринок' : fmtPct(product.market_gap_pct)}
        </span>
    );
};

const share = (part, whole) => (whole ? `${Math.round(part / whole * 100)}%` : '—');

const OwnProductsPage = () => {
    // Останні відомі дані з кешу — сторінка показує їх одразу, а свіжі
    // підтягуються у фоні (див. cachedGet у services/api.js).
    const cachedList = ownProductsAPI.peekList('', PAGE_SIZE, 0, '');
    const [products, setProducts] = useState(cachedList?.products || []);
    const [total, setTotal] = useState(cachedList?.total || 0);
    const [summary, setSummary] = useState(() => peekCache('/own_products/summary', {}) || null);
    const [sources, setSources] = useState(() => (peekCache('/offers/sources', {})?.sources || []).filter(s => s.is_active));
    const [search, setSearch] = useState('');
    const [position, setPosition] = useState('');
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(!cachedList);
    const [selected, setSelected] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importMsg, setImportMsg] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const data = await offersAPI.sources();
                setSources((data.sources || []).filter(s => s.is_active));
            } catch (err) {
                console.error('Failed to load sources:', err);
            }
        })();
    }, []);

    const loadSummary = useCallback(async (opts) => {
        try {
            const data = await ownProductsAPI.summary(opts);
            syncServerClock(data.now);
            setSummary(data);
        } catch (err) {
            console.error('Failed to load catalog summary:', err);
        }
    }, []);

    useEffect(() => { loadSummary(); }, [loadSummary]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await ownProductsAPI.list(search, PAGE_SIZE, page * PAGE_SIZE, position);
            setProducts(data.products || []);
            setTotal(data.total || 0);
        } catch (err) {
            console.error('Failed to load own products:', err);
            setProducts([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [search, page, position]);

    // Затримка — лише поки користувач друкує в пошуку.
    const lastSearch = useRef(search);
    useEffect(() => {
        const typing = lastSearch.current !== search;
        lastSearch.current = search;
        const t = setTimeout(load, typing ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, search]);

    const pickPosition = (key) => {
        setPosition(prev => (prev === key ? '' : key));
        setPage(0);
    };

    const handleExport = async () => {
        setExporting(true);
        setImportMsg(null);
        try {
            saveBlob(await ownProductsAPI.exportXlsx(), `catalog_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (err) {
            setImportMsg({ type: 'error', text: 'Не вдалося сформувати файл: ' + (err.message || '') });
        } finally {
            setExporting(false);
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = '';
        setImporting(true);
        setImportMsg(null);
        try {
            const workbook = XLSX.read(await file.arrayBuffer());
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(sheet);
            const rows = raw.map(r => {
                const trimmed = {};
                IMPORT_COLUMNS.forEach(c => { if (r[c] !== undefined) trimmed[c] = r[c]; });
                return trimmed;
            });
            const res = await ownProductsAPI.importRows(rows);
            const mapped = res.mapping ? res.mapping.products : 0;
            setImportMsg({
                type: 'ok',
                text: <>
                    <b>Завантажено {fmtNum(res.saved)} товарів</b> з {fmtNum(res.rows)} рядків.
                    Зіставлено з конкурентами: {fmtNum(mapped)}.
                    {res.skipped_no_size ? ` Пропущено без розміру в назві: ${fmtNum(res.skipped_no_size)}.` : ''}
                </>,
            });
            setPage(0);
            load();
            loadSummary({ fresh: true });
        } catch (err) {
            setImportMsg({ type: 'error', text: err.response?.data?.detail || err.message });
        } finally {
            setImporting(false);
        }
    };

    const s = summary;
    const sourceIndex = (key) => Math.max(0, sources.findIndex(x => x.source_key === key));

    const renderCompetitorPrice = (product, sourceKey) => {
        const c = product.competitors?.[sourceKey];
        if (!c) return <span className="muted">—</span>;
        if (c.price == null) {
            return <span className="muted" style={{ fontSize: 12 }}>{c.in_stock === 0 ? 'немає' : 'немає в базі'}</span>;
        }
        // Застаріла ціна — не ринок: показуємо, але приглушено, з датою.
        if (c.stale) {
            return (
                <span className="muted" title={`Не бачили ${timeAgo(c.last_seen_at) || 'давно'} — не враховано в ринковій ціні`}
                      style={{ textDecoration: 'line-through', textDecorationColor: 'var(--line-strong)' }}>
                    {fmtNum(c.price)}
                </span>
            );
        }
        const isMin = product.market_min != null && c.price === product.market_min;
        return (
            <>
                <span style={{ color: isMin ? 'var(--text)' : 'var(--text-2)', fontWeight: isMin ? 700 : 400 }}>
                    {fmtNum(c.price)}
                </span>
                {c.availability && c.availability !== ORDINARY_AVAILABILITY && (
                    <span style={availabilityBadge}>{c.availability}</span>
                )}
            </>
        );
    };

    return (
        <div className="ui-page">
            <PageHeader
                eyebrow={s?.updated_at ? `Прайс оновлено ${fmtDay(s.updated_at)}` : 'Свої товари проти ринку'}
                title="Мій каталог"
                actions={<>
                    <button className="ui-btn" onClick={handleExport} disabled={exporting || !s?.total}>
                        <Icon name="download" />
                        {exporting ? 'Формую…' : 'Excel'}
                    </button>
                    <FileButton primary accept=".xlsx,.xls" onChange={handleImport} disabled={importing}>
                        {importing ? 'Завантаження…' : 'Завантажити прайс'}
                    </FileButton>
                </>}
            />

            {importMsg && (
                <Alert kind={importMsg.type} onClose={() => setImportMsg(null)}>{importMsg.text}</Alert>
            )}

            {s && s.total > 0 && (
                <section className="ui-tiles" aria-label="Позиція відносно ринку">
                    <Tile label="Зіставлено з ринком" value={fmtNum(s.matched)} chip={share(s.matched, s.total)}
                          note={`з ${fmtNum(s.total)} товарів каталогу`}
                          active={position === 'matched'} onClick={() => pickPosition('matched')} />
                    <Tile label="Дешевші за ринок" value={fmtNum(s.cheaper)} chip={share(s.cheaper, s.matched)} chipKind="good"
                          note="є простір підняти ціну"
                          active={position === 'cheaper'} onClick={() => pickPosition('cheaper')} />
                    <Tile label="В ринку" value={fmtNum(s.even)} chip={share(s.even, s.matched)}
                          note={`різниця менше ±${String(s.even_pct).replace('.', ',')}%`}
                          active={position === 'even'} onClick={() => pickPosition('even')} />
                    <Tile label="Дорожчі за ринок" value={fmtNum(s.dearer)} chip={share(s.dearer, s.matched)} chipKind="bad"
                          note="ризик втратити продаж"
                          active={position === 'dearer'} onClick={() => pickPosition('dearer')} />
                    <Tile label="Середня маржа" value={s.avg_margin_pct != null ? `${String(s.avg_margin_pct).replace('.', ',')}%` : '—'}
                          note={`до закупівлі, по ${fmtNum(s.with_cost)} товарах`} />
                </section>
            )}

            {!loading && !search && !position && total === 0 ? (
                <div className="ui-empty">
                    <p style={{ margin: 0, color: 'var(--text)', fontWeight: 600 }}>Каталог порожній</p>
                    <p style={{ margin: '8px 0 0', fontSize: 14 }}>
                        Завантаж прайс кнопкою вгорі — товари з'являться тут разом з цінами конкурентів.
                    </p>
                </div>
            ) : (
                <section className="ui-card ui-card--flush">
                    <div className="ui-card-head">
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                            <h2 className="ui-section-title" style={{ fontSize: 20 }}>{LIST_TITLES[position]}</h2>
                            <span className="muted num" style={{ fontSize: 14 }}>{fmtNum(total)}</span>
                            {(position === 'cheaper' || position === 'dearer') && (
                                <span className="muted" style={{ fontSize: 13 }}>· спершу найбільший розрив</span>
                            )}
                        </div>
                        <div className="ui-toolbar">
                            {s?.unmatched > 0 && (
                                <button className={`ui-pill${position === 'unmatched' ? ' is-on' : ''}`}
                                        onClick={() => pickPosition('unmatched')}>
                                    Без цін конкурентів <span className="ui-pill-count">{fmtNum(s.unmatched)}</span>
                                </button>
                            )}
                            <SearchField value={search} onChange={v => { setPage(0); setSearch(v); }}
                                         placeholder="Пошук за назвою або брендом" style={{ minWidth: 280 }} />
                        </div>
                    </div>

                    {loading && products.length === 0 ? <LoadingSpinner /> : (
                        <div className="ui-table-wrap" style={{ marginTop: 8, opacity: loading ? 0.5 : 1, transition: 'opacity .2s' }}>
                            <table className="ui-table">
                                <thead>
                                    <tr>
                                        <th>Товар</th>
                                        <th className="r">Вхід</th>
                                        <th className="r">Наша ціна</th>
                                        <th className="r">Маржа</th>
                                        <th>До ринку</th>
                                        {sources.map((src, i) => (
                                            <th key={src.source_key} className="r sep">
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    <SourceDot sourceKey={src.source_key} index={i} />{src.title}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.length === 0 ? (
                                        <tr><td colSpan={5 + sources.length} className="muted">Нічого не знайдено.</td></tr>
                                    ) : products.map(p => (
                                        <tr key={p.my_id} className="is-clickable" onClick={() => setSelected(p)}>
                                            <td style={{ maxWidth: 420 }}>
                                                <button className="own-name ellipsis" onClick={e => { e.stopPropagation(); setSelected(p); }}>
                                                    {p.name || `Товар ${p.my_id}`}
                                                </button>
                                                <div className="ui-sub">
                                                    ID {p.my_id} · <span style={{ color: p.qty > 0 ? undefined : 'var(--bad)' }}>
                                                        {p.qty > 0 ? `залишок ${p.qty}` : 'немає на складі'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="r muted-2">{fmtNum(p.cost)}</td>
                                            <td className="r nowrap" style={{ fontSize: 15, fontWeight: 700 }}>{fmtMoney(p.price)}</td>
                                            <td className="r nowrap">
                                                {p.margin != null ? (
                                                    <>
                                                        <div style={{ fontWeight: 600, color: p.margin > 0 ? undefined : 'var(--bad)' }}>{fmtNum(p.margin)}</div>
                                                        {p.margin_pct != null && <div className="ui-sub">{String(p.margin_pct).replace('.', ',')}%</div>}
                                                    </>
                                                ) : <span className="muted">—</span>}
                                            </td>
                                            <td><PositionChip product={p} /></td>
                                            {sources.map(src => (
                                                <td key={src.source_key} className="r sep nowrap">
                                                    {renderCompetitorPrice(p, src.source_key)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
                </section>
            )}

            {selected && (
                <ProductSheet product={selected} sources={sources} sourceIndex={sourceIndex}
                              onClose={() => setSelected(null)} />
            )}
        </div>
    );
};

// ── Картка товару: «ми проти ринку» ─────────────────────────

const PERIODS = [
    { key: 7, label: '7 днів' },
    { key: 30, label: '30 днів' },
    { key: 90, label: '90 днів' },
    { key: 0, label: 'Увесь час' },
];

const DAY = 86400000;
const toTime = (s) => new Date(String(s).replace(' ', 'T')).getTime();
const fmtAxisDate = (t) => {
    const d = new Date(t);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const ProductSheet = ({ product, sources, sourceIndex, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(30);
    const colors = useChartColors();

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                setData(await ownProductsAPI.priceHistory(product.my_id));
            } catch (err) {
                console.error('Failed to load history:', err);
                setData(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [product]);

    const titleOf = (key) => sources.find(x => x.source_key === key)?.title || data?.competitors?.[key]?.title || key;

    // Найдешевший свіжий конкурент — той самий, від якого рахується позиція.
    const cheapest = useMemo(() => {
        let best = null;
        Object.entries(product.competitors || {}).forEach(([key, c]) => {
            if (c.price != null && !c.stale && (best == null || c.price < best.price)) best = { key, ...c };
        });
        return best;
    }, [product]);

    // Ціна тримається до наступної зміни, тож кожну лінію доводимо до «зараз»
    // (або до останнього разу, коли пропозицію бачили) і заповнюємо проміжки
    // попереднім значенням. Порожнє значення (null) — товару не було в наявності.
    const series = useMemo(() => {
        if (!data) return { rows: [], lines: [] };
        const now = Date.now();
        const lines = [{ key: 'own', name: 'Наша ціна', color: colors.accent, width: 3.5, current: product.price }];
        const events = [];
        (data.own || []).forEach(p => events.push({ t: toTime(p.recorded_at), key: 'own', price: p.price != null ? Number(p.price) : null }));
        const end = { own: now };

        Object.entries(data.competitors || {}).forEach(([key, info]) => {
            if (!(info.history || []).length) return;
            info.history.forEach(p => events.push({ t: toTime(p.recorded_at), key, price: p.price }));
            end[key] = info.stale && info.last_seen_at ? toTime(info.last_seen_at) : now;
            lines.push({
                key, name: titleOf(key), color: sourceColor(key, sourceIndex(key)), width: 2,
                current: info.price, stale: info.stale,
            });
        });

        events.sort((a, b) => a.t - b.t);
        const times = [...new Set([...events.map(e => e.t), now])].sort((a, b) => a - b);
        const last = {};
        const rows = times.map(t => {
            events.filter(e => e.t === t).forEach(e => { last[e.key] = e.price; });
            const row = { t };
            Object.keys(last).forEach(k => { if (t <= end[k]) row[k] = last[k]; });
            return row;
        });

        const cutoff = period ? now - period * DAY : null;
        if (!cutoff) return { rows, lines };
        // Значення на початок періоду — з останньої точки до нього.
        const before = rows.filter(r => r.t < cutoff).pop();
        const inside = rows.filter(r => r.t >= cutoff);
        return { rows: before ? [{ ...before, t: cutoff }, ...inside] : inside, lines };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, period, colors, product]);

    const noHistory = !loading && series.rows.length < 2;
    const gap = product.market_gap_pct;
    const diff = cheapest && product.price != null ? Math.round(cheapest.price - Number(product.price)) : null;
    const posTone = product.market_pos === 'cheaper' ? 'good' : product.market_pos === 'dearer' ? 'bad' : null;

    return (
        <Modal
            onClose={onClose}
            width={920}
            title={product.name || `Товар ${product.my_id}`}
            head={
                <div className="ui-toolbar" style={{ gap: 8, marginBottom: 12 }}>
                    <span className="ui-chip">ID {product.my_id}</span>
                    {product.width && <span className="ui-chip">{product.width}/{product.profil} {product.diametr}</span>}
                    {product.season && <span className="ui-chip">{seasonLabel(product.season)}</span>}
                    <span className={`ui-chip ${product.qty > 0 ? 'ui-chip--good' : 'ui-chip--bad'}`}>
                        {product.qty > 0 ? `залишок ${product.qty}` : 'немає на складі'}
                    </span>
                </div>
            }
        >
            <div className="own-stats">
                <div className="own-stat">
                    <span className="own-stat-label">Наша ціна</span>
                    <span className="own-stat-value">{fmtMoney(product.price)}</span>
                    <span className="own-stat-note">
                        вхід {fmtNum(product.cost)}
                        {product.margin != null && <> · маржа {fmtMoney(product.margin)}{product.margin_pct != null && ` (${String(product.margin_pct).replace('.', ',')}%)`}</>}
                    </span>
                </div>
                <div className="own-stat">
                    <span className="own-stat-label">Найдешевший конкурент</span>
                    <span className="own-stat-value">{cheapest ? fmtMoney(cheapest.price) : '—'}</span>
                    <span className="own-stat-note">
                        {cheapest ? <><SourceDot sourceKey={cheapest.key} index={sourceIndex(cheapest.key)} /> {titleOf(cheapest.key)} · {timeAgo(cheapest.last_seen_at)}</>
                                  : 'немає свіжих цін конкурентів'}
                    </span>
                </div>
                <div className={`own-stat${posTone ? ` own-stat--${posTone}` : ''}`}>
                    <span className="own-stat-label">Позиція на ринку</span>
                    <span className="own-stat-value" style={{ color: posTone ? `var(--${posTone})` : undefined }}>
                        {product.market_pos === 'even' ? '≈ ринок' : gap != null ? fmtPct(gap) : '—'}
                    </span>
                    <span className="own-stat-note">
                        {product.market_pos === 'cheaper' && diff != null && `ми дешевші — простір до +${fmtMoney(diff)}`}
                        {product.market_pos === 'dearer' && diff != null && `ми дорожчі на ${fmtMoney(-diff)}`}
                        {product.market_pos === 'even' && 'в межах ±1% від найдешевшого'}
                        {product.market_pos == null && 'немає з чим порівняти'}
                    </span>
                </div>
            </div>

            <div className="ui-card-head" style={{ marginTop: 24, marginBottom: 12 }}>
                <h3>Ми проти ринку</h3>
                <div className="ui-seg" role="group" aria-label="Період">
                    {PERIODS.map(p => (
                        <button key={p.key} className={period === p.key ? 'is-on' : ''} aria-pressed={period === p.key}
                                onClick={() => setPeriod(p.key)}>{p.label}</button>
                    ))}
                </div>
            </div>

            {loading ? <LoadingSpinner /> : noHistory ? (
                <p className="muted">
                    Історії за цей період немає — потрібно щонайменше два завантаження прайсу
                    (або збори конкурентів) у різні дні.
                </p>
            ) : (
                <>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={series.rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke={colors.line} />
                                <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                                       tickFormatter={fmtAxisDate} tick={{ fontSize: 12, fill: colors.text3 }}
                                       axisLine={false} tickLine={false} minTickGap={24} />
                                <YAxis tick={{ fontSize: 12, fill: colors.text3 }} domain={['auto', 'auto']}
                                       tickFormatter={v => fmtNum(v)} axisLine={false} tickLine={false} width={60} />
                                <Tooltip content={<HistoryTooltip lines={series.lines} />}
                                         cursor={{ stroke: colors.text3, strokeDasharray: '3 4' }} />
                                {series.lines.slice().reverse().map(l => (
                                    <Line key={l.key} type="stepAfter" dataKey={l.key} name={l.name} stroke={l.color}
                                          strokeWidth={l.width} dot={false} activeDot={{ r: l.key === 'own' ? 6 : 4, stroke: colors.surface, strokeWidth: 2 }}
                                          isAnimationActive={false} connectNulls={false} />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="ui-toolbar" style={{ gap: 8, marginTop: 12 }}>
                        {series.lines.map(l => (
                            <span key={l.key} className="ui-chip" style={{ color: 'var(--text)', fontWeight: l.key === 'own' ? 700 : 500 }}>
                                <span className="ui-dot" style={{ background: l.color, width: l.key === 'own' ? 12 : 8, height: l.key === 'own' ? 12 : 8 }} />
                                {l.name}
                                <span className="muted-2 num">{l.current != null ? fmtNum(l.current) : 'немає'}{l.stale ? ' · застаріла' : ''}</span>
                            </span>
                        ))}
                    </div>
                </>
            )}
            <p className="muted" style={{ fontSize: 13, margin: '14px 0 0' }}>
                Сходинка — момент зміни ціни: історія пишеться лише тоді, коли ціна змінилась.
                Розрив лінії — товару не було в наявності.
            </p>
        </Modal>
    );
};

const HistoryTooltip = ({ active, payload, label, lines }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    const d = new Date(label);
    return (
        <div className="own-tooltip">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {d.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long' })}
            </div>
            {lines.filter(l => l.key in row).map(l => (
                <div key={l.key} className="own-tooltip-row">
                    <span className="ui-dot" style={{ background: l.color }} />
                    <span style={{ flex: 1 }}>{l.name}</span>
                    <b className="num">{row[l.key] != null ? fmtNum(row[l.key]) : 'немає'}</b>
                </div>
            ))}
        </div>
    );
};

export default OwnProductsPage;
